"use client";

import * as React from "react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type SizeRow = {
  us: string;
  uk: string;
  eu: string;
  cm: string;
  fit: string;
  matched: boolean;
};
const brands = [{
  id: "phenolic",
  label: "Phenolic"
}, {
  id: "stainless",
  label: "Stainless"
}, {
  id: "aluminum",
  label: "Aluminum"
}, {
  id: "brass",
  label: "Brass"
}];
const phenolicRows: SizeRow[] = [{
  us: "1/16",
  uk: "1.6mm",
  eu: "Indoor",
  cm: "Standard",
  fit: "General purpose engraving",
  matched: false
}, {
  us: "1/8",
  uk: "3.2mm",
  eu: "Outdoor",
  cm: "Heavy",
  fit: "UV resistant surface",
  matched: false
}, {
  us: "1/4",
  uk: "6.4mm",
  eu: "Industrial",
  cm: "Rigid",
  fit: "High impact resistance",
  matched: true
}, {
  us: "3/8",
  uk: "9.5mm",
  eu: "Structural",
  cm: "Solid",
  fit: "Maximum durability",
  matched: false
}, {
  us: "1/2",
  uk: "12.7mm",
  eu: "Heavy",
  cm: "Dense",
  fit: "Extreme environment",
  matched: false
}];
const stainlessRows: SizeRow[] = [{
  us: "304",
  uk: "0.5mm",
  eu: "Standard",
  cm: "Matte",
  fit: "Corrosion resistant",
  matched: false
}, {
  us: "316",
  uk: "0.8mm",
  eu: "Marine",
  cm: "Brushed",
  fit: "Saltwater environment",
  matched: false
}, {
  us: "316L",
  uk: "1.2mm",
  eu: "Premium",
  cm: "Mirror",
  fit: "High-grade finish",
  matched: true
}, {
  us: "430",
  uk: "1.5mm",
  eu: "Economy",
  cm: "Satin",
  fit: "Magnetic properties",
  matched: false
}, {
  us: "Duplex",
  uk: "2.0mm",
  eu: "Heavy",
  cm: "Polished",
  fit: "Structural strength",
  matched: false
}];
const aluminumRows: SizeRow[] = [{
  us: "0.020",
  uk: "Clear",
  eu: "Anodized",
  cm: "Light",
  fit: "Standard identification",
  matched: false
}, {
  us: "0.032",
  uk: "Black",
  eu: "Anodized",
  cm: "Medium",
  fit: "High contrast laser",
  matched: false
}, {
  us: "0.040",
  uk: "Blue",
  eu: "Anodized",
  cm: "Rigid",
  fit: "Color coded systems",
  matched: true
}, {
  us: "0.063",
  uk: "Red",
  eu: "Anodized",
  cm: "Heavy",
  fit: "Safety labeling",
  matched: false
}, {
  us: "0.125",
  uk: "Gold",
  eu: "Anodized",
  cm: "Solid",
  fit: "Premium aesthetic",
  matched: false
}];
const brassRows: SizeRow[] = [{
  us: "0.020",
  uk: "Raw",
  eu: "Polished",
  cm: "Thin",
  fit: "Classic plaque look",
  matched: false
}, {
  us: "0.032",
  uk: "Satin",
  eu: "Lacquered",
  cm: "Medium",
  fit: "Oxidation resistant",
  matched: false
}, {
  us: "0.040",
  uk: "Antique",
  eu: "Patina",
  cm: "Heavy",
  fit: "Architectural grade",
  matched: true
}, {
  us: "0.063",
  uk: "Brushed",
  eu: "Clear",
  cm: "Rigid",
  fit: "High-end signage",
  matched: false
}, {
  us: "0.125",
  uk: "Mirror",
  eu: "Polished",
  cm: "Solid",
  fit: "Premium finish",
  matched: false
}];
const brandData: Record<string, SizeRow[]> = {
  phenolic: phenolicRows,
  stainless: stainlessRows,
  aluminum: aluminumRows,
  brass: brassRows
};
function SizeTable({
  rows
}: {
  rows: SizeRow[];
}) {
  return <TooltipProvider delayDuration={150}>
      <Table className="border-collapse">
        <TableHeader>
          <TableRow className="border-b border-border hover:bg-transparent">
            <TableHead className="font-default text-xs uppercase tracking-[0.18em] text-muted-foreground sticky left-0 bg-background">
              Grade
            </TableHead>
            <TableHead className="font-default text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Spec
            </TableHead>
            <TableHead className="font-default text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Type
            </TableHead>
            <TableHead className="font-default text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Finish
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, i) => <Tooltip key={i}>
              <TooltipTrigger asChild>
                <TableRow data-index={i} className="border-b border-dashed border-border odd:bg-card/40 transition-colors duration-200 hover:bg-secondary/40 cursor-default">
                  <TableCell className={row.matched ? "sticky left-0 bg-primary text-primary-foreground font-default font-semibold text-base" : "sticky left-0 bg-background font-default text-base text-foreground"}>
                    {row.us}
                  </TableCell>
                  <TableCell className="font-default text-base text-foreground">
                    {row.uk}
                  </TableCell>
                  <TableCell className="font-default text-base text-foreground">
                    {row.eu}
                  </TableCell>
                  <TableCell className="font-default text-base text-foreground">
                    {row.cm}
                  </TableCell>
                </TableRow>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-primary text-primary-foreground font-default text-[0.7rem] uppercase tracking-[0.12em]">
                {row.fit}
              </TooltipContent>
            </Tooltip>)}
        </TableBody>
      </Table>
    </TooltipProvider>;
}
export default function MaterialBreadth() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="1935" id="material-breadth" className="bg-background text-foreground py-24 md:py-36 px-5 md:px-10">
      <div className="mx-auto max-w-[1320px]">
        {}
        <div ref={ref} className={"relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-end transition-all duration-700 " + (isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6")}>
          {}
          <span aria-hidden="true" className="pointer-events-none absolute -top-10 left-0 select-none font-serif font-bold text-foreground/[0.06] leading-none text-[8rem] md:text-[12rem]">
            02
          </span>

          <div className="lg:col-span-8 relative z-10">
            <p className="font-default text-xs md:text-sm uppercase tracking-[0.22em] text-muted-foreground mb-6">
              § 02 / MATERIAL SPECIFICATIONS
            </p>
            <h2 className="font-default font-semibold leading-[0.98] tracking-[-0.02em] text-[clamp(2rem,4.5vw,3.75rem)] text-foreground">
              Industrial &middot; Technical &middot; Precision,{" "}
              <span className="relative inline-block">
                Material
                <span className="absolute -bottom-1 left-0 h-[3px] w-full bg-primary" />
                <span className="absolute -bottom-1 right-[-6px] h-[7px] w-[7px] rounded-lg bg-primary" />
              </span>{" "}
              Standards
            </h2>
            <p className="mt-6 max-w-xl font-default text-base md:text-lg leading-relaxed text-muted-foreground">
              Explore our comprehensive material database. Select a category to view technical specifications and application suitability for your project requirements.
            </p>
          </div>

          <div className="lg:col-span-4 relative z-10">
            <Tabs defaultValue="phenolic" className="w-full">
              

              {}
              {brands.map((brand, i) => <TabsContent key={brand.id} data-index={i} value={brand.id} className="mt-10 animate-in fade-in-0 duration-300">
                  {}
                  <div className="block md:hidden">
                    <div className="rounded-lg border border-dashed border-border p-1">
                      <ScrollArea className="w-full whitespace-nowrap">
                        <div className="min-w-[480px] p-3">
                          <SizeTable rows={brandData[brand.id]} />
                        </div>
                        <ScrollBar orientation="horizontal" />
                      </ScrollArea>
                    </div>
                  </div>
                </TabsContent>)}
            </Tabs>
          </div>
        </div>

        {}
        <div className="my-10 hidden md:block border-t border-dashed border-border" />

        {}
        <div className="hidden md:block">
          <Tabs defaultValue="phenolic" className="w-full">
            <TabsList className="mb-8 inline-flex h-auto gap-1 bg-secondary p-1 rounded-lg">
              {brands.map((brand, i) => <TabsTrigger key={brand.id} data-index={i} value={brand.id} className="font-default text-xs uppercase tracking-[0.12em] px-5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-lg">
                  {brand.label}
                </TabsTrigger>)}
            </TabsList>

            {brands.map((brand, i) => <TabsContent key={brand.id} data-index={i} value={brand.id} className="animate-in fade-in-0 duration-300">
                <div className="rounded-lg border border-dashed border-border p-4 md:p-6 bg-card/20">
                  <SizeTable rows={brandData[brand.id]} />
                </div>
                <p className="mt-4 font-default text-[0.7rem] uppercase tracking-[0.12em] text-muted-foreground">
                  Highlighted rows represent our most requested industrial grade specifications
                </p>
              </TabsContent>)}
          </Tabs>
        </div>
      </div>
    </section>;
}